import Script from 'next/script';

import { getProviders, signIn, useSession } from 'next-auth/react';

import getConfig from 'config/public';
import { useEventContext } from 'context/EventContext';
import detachTask from 'helpers/detachTask';
import { getG2AuthorizationParams } from 'services/auth/g2AuthorizationParams';
import {
  appendOneTapAttemptId,
  clearOneTapPendingAttempt,
  createOneTapAttemptId,
  storeOneTapPendingAttempt,
} from 'shared-utils/oneTapAnalytics';
import { runLoginRedirect } from 'shared-utils/unifiedLoginAnalytics';

import { buildGetAppMarketingEvent } from './constants';

const GSI_CLIENT_SRC = 'https://accounts.google.com/gsi/client';
const G2_ONE_TAP_CALLBACK_PATH = '/auth/partner_google_one_tap/callback';
const ONE_TAP_CLOSE_DELAY_MS = 250;

/**
 * Build G2's Google One Tap login endpoint, carrying the NextAuth
 * `/oauth/authorize` URL as `return_to`.
 *
 * The G2 origin is taken from the authorize URL itself (which NextAuth builds
 * from the `g2` provider) so it stays correct across environments without extra
 * client config. On tap, we POST the credential to this endpoint (query string
 * preserved). G2 verifies it, establishes the g2.com session, then redirects to
 * `return_to` — the normal OAuth code exchange that logs the user into GetApp too.
 */
export const buildG2OneTapCallbackUrl = (authorizeUrl) => {
  const { origin } = new URL(authorizeUrl);

  return `${origin}${G2_ONE_TAP_CALLBACK_PATH}?return_to=${encodeURIComponent(
    authorizeUrl
  )}`;
};

let initialEventSent = false;
let oneTapAttemptId = null;
let gsiInitialized = false;

export const getOneTapAttemptId = () => {
  oneTapAttemptId ||= createOneTapAttemptId();
  return oneTapAttemptId;
};

const getOneTapLoginUri = async () => {
  // Create the Auth.js state/PKCE transaction only after Google returns a
  // credential; the prompt must not retain an authorize URL that another
  // sign-in can invalidate while it is open.
  const providers = await getProviders();
  if (!providers?.g2) throw new Error('G2 auth provider is unavailable');

  const result = await signIn(
    'g2',
    {
      redirect: false,
      callbackUrl: window.location.href,
    },
    getG2AuthorizationParams(false)
  );
  if (!result?.url) throw new Error('G2 authorize URL was not returned');

  return buildG2OneTapCallbackUrl(
    appendOneTapAttemptId(result.url, getOneTapAttemptId())
  );
};

export const resetOneTapForTesting = () => {
  initialEventSent = false;
  oneTapAttemptId = null;
  gsiInitialized = false;
};

/**
 * Top-level form POST of the One Tap credential to G2's cross-origin endpoint.
 * Mirrors the POST GSI made for `login_uri`, minus g_csrf_token: that cookie is
 * set on getapp's origin and never reaches g2.com, so G2 validates this
 * cross-site POST via the Origin header (Extended::GoogleOneTap#valid_partner_origin?).
 * Must be a real form submit (top-level navigation) so G2 can set the g2.com
 * session cookie and 302 to `return_to` — a fetch/XHR can't follow that chain.
 * Query string (return_to) stays in the action URL; the credential goes in the body.
 */
const submitCredentialToG2 = (loginUri, credential) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = loginUri;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'credential';
  input.value = credential;

  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

/**
 * Google One Tap via the GSI JS API (`google.accounts.id.initialize` + `.prompt`
 * + JS callback). On account selection, create the Auth.js transaction then
 * relay the credential to G2 via a top-level form POST. Note: the `g_csrf_token`
 * double-submit cookie is set on the PAGE's origin (getapp.com), so it never
 * reaches g2.com — G2's One Tap endpoint validates this cross-site POST via the
 * `Origin` header against the partner app's registered redirect_uri origins
 * instead (see `Extended::GoogleOneTap#valid_partner_origin?`).
 */
const OneTapScript = ({ user }) => {
  const { sendEvent } = useEventContext();
  const { googleClientId } = getConfig();
  const { status } = useSession();

  const safeSendEvent = (event) => {
    try {
      return Promise.resolve(sendEvent(event)).catch(() => undefined);
    } catch {
      return Promise.resolve();
    }
  };
  const sendDetachedEvent = (event) => {
    safeSendEvent(event);
  };

  // Gate on resolved-unauthenticated: skip loading state so a logged-in user's
  // session resolution never triggers signIn/analytics/the GSI prompt.
  const enabled =
    status === 'unauthenticated' && !user && Boolean(googleClientId);

  if (!enabled) {
    return null;
  }

  const handleGsiLoad = async () => {
    if (!window.google) {
      return;
    }

    if (gsiInitialized) return;
    gsiInitialized = true;

    clearOneTapPendingAttempt();
    const attemptId = getOneTapAttemptId();
    let credentialReturned = false;
    let credentialSubmitted = false;
    let closeReported = false;
    let closeReportTimer;
    let displayedReported = false;
    let failedToDisplayReported = false;

    const cancelScheduledClose = () => {
      if (closeReportTimer === undefined) {
        return;
      }

      window.clearTimeout(closeReportTimer);
      closeReportTimer = undefined;
    };

    const reportOneTapClosed = () => {
      closeReportTimer = undefined;
      if (closeReported || credentialReturned || credentialSubmitted) {
        return;
      }

      closeReported = true;
      safeSendEvent(buildGetAppMarketingEvent('form-closed', attemptId));
    };

    const scheduleOneTapClosed = () => {
      if (
        closeReported ||
        credentialReturned ||
        credentialSubmitted ||
        closeReportTimer !== undefined
      ) {
        return;
      }

      // Leave time for a concurrent credential handoff before reporting a skip.
      closeReportTimer = window.setTimeout(
        reportOneTapClosed,
        ONE_TAP_CLOSE_DELAY_MS
      );
    };

    const reportOneTapDisplayed = () => {
      displayedReported = true;
      detachTask(
        sendDetachedEvent,
        buildGetAppMarketingEvent('form-open', attemptId)
      );
    };

    const reportOneTapFailedToDisplay = () => {
      failedToDisplayReported = true;
      detachTask(
        sendDetachedEvent,
        buildGetAppMarketingEvent('form-fail-to-display', attemptId)
      );
    };

    if (!initialEventSent) {
      initialEventSent = true;
      detachTask(
        sendDetachedEvent,
        buildGetAppMarketingEvent('form-try-to-display', attemptId)
      );
    }

    window.google.accounts.id.initialize({
      client_id: googleClientId,
      cancel_on_tap_outside: false,
      use_fedcm_for_prompt: true,
      callback: (response) => {
        if (
          credentialSubmitted ||
          typeof response?.credential !== 'string' ||
          !response.credential.trim()
        ) {
          return;
        }

        cancelScheduledClose();
        credentialSubmitted = true;
        runLoginRedirect(async () => {
          const dispatch = Promise.allSettled([
            safeSendEvent(buildGetAppMarketingEvent('form-started', attemptId)),
          ]);
          const timeout = new Promise((resolve) =>
            window.setTimeout(resolve, 500)
          );
          await Promise.race([dispatch, timeout]);
          const loginUri = await getOneTapLoginUri();
          storeOneTapPendingAttempt(attemptId);
          submitCredentialToG2(loginUri, response.credential);
        }).catch(() => undefined);
      },
    });

    // FedCM omits display moments and skipped reasons. Preserve the legacy
    // form-closed definition: a skipped prompt, which can include failed attempts.
    // https://developers.google.com/identity/gsi/web/guides/fedcm-migration
    window.google.accounts.id.prompt((notification) => {
      credentialReturned ||=
        notification.getDismissedReason?.() === 'credential_returned';
      if (credentialSubmitted || credentialReturned) {
        cancelScheduledClose();
        return;
      }

      if (!failedToDisplayReported && notification.isNotDisplayed?.()) {
        reportOneTapFailedToDisplay();
        return;
      }

      if (!displayedReported && notification.isDisplayed?.()) {
        reportOneTapDisplayed();
      }

      if (notification.isSkippedMoment?.()) {
        scheduleOneTapClosed();
      }
    });
  };

  return (
    <Script
      src={GSI_CLIENT_SRC}
      strategy="lazyOnload"
      data-testid="googleidentityservice"
      onReady={handleGsiLoad}
    />
  );
};

export default OneTapScript;
